import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import StripeCheckout from 'react-stripe-checkout';
import type { IProps } from './billing-summary-modal-container';
import Modal from '../../../../../../../../shared/design-system/components/atoms/modal';
import Button from '../../../../../../../../shared/design-system/components/atoms/button';
import { accessibleOnClick } from '../../../../../../../../shared/utils/accessible-on-click';
import Input from '../../../../../../../../shared/design-system/components/input/input';
import { validate } from '../../../../../../../../shared/design-system/components/molecules/upgrade-plan-billing-section/validator';
import { isRequestPending } from '../../../../utils/helper';
import { Images } from '../../../../../../../../shared/app-constants';
import { getSubtotalDiscountAmount } from '../../../../../../../../shared/design-system/components/molecules/upgrade-plan-billing-section/helper/helper';
import { executeOnRequestStatus } from '../../../../../../../../shared/utils/execute-on-request-status';
import { supportUrls } from '../../../../../../../../shared/utils/urls';
import { getTotalAmountForOutreachPlan } from './utils/helper';
import ImageIcon from '../../../../../../../../shared/components/images/image-icon';
import { AnalyticsEvents } from '../../../../../../../../shared/enums/analytics';
import { filterDecimal } from '../../../../../../../../shared/utils/filter-decimal';
import { getUserTrackingId } from '../../../../../../../../shared/utils/user-details';

const BillingSummaryModal: React.FC<IProps> = ({
  show,
  onClose,
  selectedPlanDetails,
  couponCodeDiscount,
  couponCodeDiscountType,
  onPurchase,
  sendGetAdminDetailsRequest,

  //* Request Response State
  sendApplyCouponCodeRequestMessage,
  isSendApplyCouponCodeRequestError,
  sendApplyCouponCodeRequestStatus,
  sendPurchaseInboxAnalyzerSubscriptionRequestStatus,
  sendGetPurchaseInboxAnalyzerModifySubscriptionRequestStatus,
  sendGetInboxAnalyzerCalculatePayResponse,
  sendGetInboxAnalyzerCalculatePayRequestStatus,
  //* API Request
  sendApplyCouponCodeRequest,
  //* Redux Util Function
  deleteAppliedCouponCode,
  currentPlanName,
  hasAccountSubscription,
  adminSettings,
}) => {
  const { t } = useTranslation();
  const totalAmount = selectedPlanDetails?.amount;
  const planId = Number(selectedPlanDetails?.id);
  const planName = selectedPlanDetails?.planName;
  const isApplyCouponCodeInProgress = isRequestPending(
    sendApplyCouponCodeRequestStatus,
  );
  const isPurchaseInProgress =
    isRequestPending(sendPurchaseInboxAnalyzerSubscriptionRequestStatus) ||
    isRequestPending(
      sendGetPurchaseInboxAnalyzerModifySubscriptionRequestStatus,
    );

  const totalAmountToPay: number = getTotalAmountForOutreachPlan({
    totalAmount: Number(totalAmount),
    discountType: couponCodeDiscountType,
    discount: couponCodeDiscount,
    sendGetInboxAnalyzerCalculatePayResponse,
  });

  const [havePromoCode, setHavePromoCode] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [error, setError] = useState<string>('');
  const [applyDefaultCouponCode, setApplyDefaultCouponCode] = useState(false);

  const userHasPromoCode = (value) => {
    setHavePromoCode(value);
  };

  const onCouponCodeChange = (value) => {
    setCouponCode(value);
  };

  const handleDeleteAppliedCouponCode = () => {
    onCouponCodeChange('');
    deleteAppliedCouponCode();
    userHasPromoCode(false);
  };

  const onApplyCouponCode = (e) => {
    e?.preventDefault();
    const res: any = adminSettings;
    const hasReferralCode =
      Array.isArray(res) &&
      res.find((r) => r.code === 'cello-referral-code')?.value;
    if (couponCode === 'REFERRAL10') {
      if (!hasReferralCode) {
        setError('ENTER A VALID COUPON CODE');
        return;
      }
    }
    let isError = false;

    const checkError = validate('couponCode', couponCode);
    isError = isError || !!checkError;
    setError(checkError);

    if (isError) {
      return;
    }

    sendApplyCouponCodeRequest({
      couponCode,
      amount: Number(totalAmountToPay),
      planId,
    });
  };

  const onPurchaseHandler = (token) => {
    onPurchase(token, couponCode);
  };

  const handleModalClose = () => {
    if (applyDefaultCouponCode) {
      handleDeleteAppliedCouponCode();
    }
    setApplyDefaultCouponCode(false);
    onClose();
  };

  const handlePayNowAnalyticsEvent = () => {
    window.analytics?.track?.({
      userId: getUserTrackingId(),
      event: AnalyticsEvents.ClickedOnPayNow,
      properties: {
        Source: 'Subscription',
        current_plan: currentPlanName,
        'Clicked Plan': planName,
      },
    });
  };

  useEffect(() => {
    sendGetAdminDetailsRequest();
  }, []);

  useEffect(() => {
    if (show && adminSettings) {
      const res: any = adminSettings;
      // eslint-disable-next-line eqeqeq
      const hasReferralCode =
        Array.isArray(res) &&
        res.find((r) => r.code === 'cello-referral-code')?.value;
      if (!hasAccountSubscription && hasReferralCode) {
        setCouponCode('REFERRAL10');
        setApplyDefaultCouponCode(true);
        userHasPromoCode(true);
      }
    }
  }, [show, adminSettings]);

  useEffect(() => {
    if (show && applyDefaultCouponCode) {
      onApplyCouponCode(null);
    }
  }, [show, applyDefaultCouponCode]);

  // Purchase subscription
  useEffect(() => {
    executeOnRequestStatus({
      status: sendPurchaseInboxAnalyzerSubscriptionRequestStatus,
      onFailed: () => {
        handleDeleteAppliedCouponCode();
      },
    });
  }, [sendPurchaseInboxAnalyzerSubscriptionRequestStatus]);

  // Modify subscription
  useEffect(() => {
    executeOnRequestStatus({
      status: sendGetPurchaseInboxAnalyzerModifySubscriptionRequestStatus,
      onFailed: () => {
        handleDeleteAppliedCouponCode();
      },
    });
  }, [sendGetPurchaseInboxAnalyzerModifySubscriptionRequestStatus]);

  const renderCouponCodeView = () => (
    <div className="promo-code-container">
      {havePromoCode && (
        <div className="coupon-code-input-wrapper mt-3">
          <Input
            name="couponCode"
            placeholder="Enter Coupon"
            value={couponCode}
            variant={
              ((error || isSendApplyCouponCodeRequestError) &&
                Input.Variant.Error) ||
              (couponCodeDiscount && Input.Variant.Succeeded)
            }
            caption={error || sendApplyCouponCodeRequestMessage}
            onChange={onCouponCodeChange}
            className="coupon-code-input"
            button={
              !isApplyCouponCodeInProgress &&
              !couponCodeDiscount && {
                buttonTitle: 'Apply',
                onClick: (e) => onApplyCouponCode(e),
              }
            }
            icons={[
              isApplyCouponCodeInProgress && {
                place: Input.IconPlace.Right,
                identifier: 'spinner-alt',
                colorDefault: true,
                className: 'icon-loading',
              },
              ...(couponCodeDiscount
                ? [
                    {
                      place: Input.IconPlace.Right,
                      identifier: 'trash',
                      colorDefault: true,
                      className: 'icon-1 icon-2',
                      onClick: () => handleDeleteAppliedCouponCode(),
                    },
                  ]
                : []),
            ]}
          />
        </div>
      )}

      {couponCodeDiscount && (
        <div className="d-flex justify-content-between subtotal-discount-amount mt-4">
          <span className="semibold-1 popover-arrow-color-txt">
            {t('labels.discount')}
          </span>
          <span className="semibold-2 popover-arrow-color-txt">{`$${filterDecimal(
            getSubtotalDiscountAmount(
              couponCodeDiscountType,
              sendGetInboxAnalyzerCalculatePayResponse?.amountToPay ||
                Number(totalAmount),
              couponCodeDiscount,
            ),
          )}`}</span>
        </div>
      )}
    </div>
  );

  const renderStripeButton = () => (
    <StripeCheckout
      token={onPurchaseHandler}
      name="Saleshandy"
      image={Images.SHIcon70}
      stripeKey={process.env.REACT_APP_CFG_PAY_01}
      amount={totalAmountToPay * 100}
      zipCode
      billingAddress
      ComponentClass="div"
    >
      <Button
        onClick={() => handlePayNowAnalyticsEvent()}
        variant={Button.Variant.Primary}
        isLoading={isPurchaseInProgress}
        disabled={isPurchaseInProgress}
        className="solid-primary-new w-100"
      >
        {t('labels.pay_now')}
      </Button>
    </StripeCheckout>
  );

  return (
    <Modal
      show={show}
      className="billing-summary-modal"
      showCloseIcon={true}
      onClose={handleModalClose}
      hideFooter={true}
      backdrop="static"
      titleContent="Billing Summary"
    >
      <div className="d-flex justify-content-between align-items-center">
        <span className="regular-1 gray-txt-12">Plan Name</span>
        <span className="regular-2 font-medium popover-arrow-color-txt">
          {planName}
        </span>
      </div>

      <div className="divider-bottom">
        <div className="d-flex justify-content-between align-items-center">
          <span className="regular-1 gray-txt-12">Total Amount</span>
          <span className="semibold-2 popover-arrow-color-txt">
            ${totalAmount}
          </span>
        </div>
        <div className="d-flex flex-column align-items-end">
          {!havePromoCode && (
            <span
              role="button"
              className="font-10 font-medium blue-txt-11 mt-1 pointer"
              {...accessibleOnClick(userHasPromoCode)}
            >
              Have a discount code?
            </span>
          )}
        </div>
        {renderCouponCodeView()}
      </div>

      <div className="d-flex justify-content-between align-items-center">
        <span className="semibold-1 popover-arrow-color-txt">
          Total Amount to pay today
        </span>
        <span className="semibold-2 popover-arrow-color-txt">
          {isRequestPending(sendGetInboxAnalyzerCalculatePayRequestStatus) ? (
            <div className="loader-container d-flex justify-content-center align-items-center">
              <ImageIcon src={Images.Loader} />
            </div>
          ) : (
            `$${totalAmountToPay}`
          )}
        </span>
      </div>

      <div className="mt-2">
        <p className="regular-1 font-10 gray-txt-15 text-center">
          Your subscription will auto-renew until you cancel. By upgrading, you
          agree to our{' '}
          <a
            href={supportUrls.refundPolicy}
            target="_blank"
            className="link"
            rel="noreferrer"
          >
            refund policy.
          </a>
        </p>
      </div>

      {renderStripeButton()}
    </Modal>
  );
};

export default BillingSummaryModal;
